import type {
  IFilterData,
  IHeader,
} from "../../../components/Organismos/Table/FilteredTable.types";
import type { IAuditChange } from "../../../interfaces/models/IAudityLogs";
import { createAuditLogDateFilterPanel } from "../../../utils/auditLogDateFilterPanel";
import {
  AuditActionType,
  AuditActionTypeEnum,
  AuditEntityType,
  AuditEntityTypeEnum,
} from "../../../Enums/LogsEnum";
import { formatFirmAuditLogNote } from "./firmAuditLogNotes";

const MAX_VALUE_LENGTH = 80;

export function formatAuditTimestamp(timestamp?: string): string {
  if (!timestamp) {
    return "—";
  }
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return String(timestamp);
  }
  return parsed.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function stringifyAuditValue(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const text =
    typeof value === "string" ? value : JSON.stringify(value) ?? String(value);
  return text.length > MAX_VALUE_LENGTH
    ? `${text.slice(0, MAX_VALUE_LENGTH)}…`
    : text;
}

/** `firmContact` → `Firm Contact`; values that already read as labels are left untouched. */
export function formatRoleLabel(role: string): string {
  const trimmed = String(role ?? "").trim();
  if (!trimmed) {
    return "";
  }
  const spaced = trimmed.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatAuditRoles(role?: string[] | string): string {
  const roles = Array.isArray(role) ? role : role ? [role] : [];
  const labels = roles.map(formatRoleLabel).filter(Boolean);
  return labels.length > 0 ? labels.join(", ") : "—";
}

export function summarizeChanges(changes?: IAuditChange[]): string {
  if (!changes?.length) {
    return "";
  }
  return changes
    .map(
      (change) =>
        `${change.field}: ${stringifyAuditValue(change.oldValue)} → ${stringifyAuditValue(change.newValue)}`,
    )
    .join("\n");
}

export function buildAuditLogFiltersData(
  nestedInModal = false,
): IFilterData[] {
  return [
    {
      name: "Action",
      fieldName: "action",
      comparison: "BY_NAME",
      disabledWrite: true,
      objectData: Object.entries(AuditActionType).map(([key, value]) => ({
        id: key,
        name: String(value),
      })),
    },
    {
      name: "Entity",
      fieldName: "entity",
      comparison: "BY_NAME",
      disabledWrite: true,
      objectData: Object.entries(AuditEntityType).map(([key, value]) => ({
        id: key,
        name: String(value),
      })),
    },
    createAuditLogDateFilterPanel({ nestedInModal }),
  ];
}

export function buildFirmModificationLogFiltersData(
  nestedInModal = false,
): IFilterData[] {
  return [
    createAuditLogDateFilterPanel({
      nestedInModal,
      pickerVariant: "firmLogs",
      dialogTitle: "Modification log date",
    }),
  ];
}

export function formatAuditDateOnly(timestamp?: string): string {
  if (!timestamp) {
    return "—";
  }
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return String(timestamp);
  }
  return parsed.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

export function formatAuditSource(origin?: string): string {
  if (!origin) {
    return "—";
  }
  if (origin === "Rewrite") {
    return "Admin Site";
  }
  return origin;
}

export function formatInformationUpdated(
  entity?: string,
  action?: string,
): string {
  const entityLabel = entity && entity !== "—" ? entity : "";
  const actionLabel = action && action !== "—" ? action : "";
  if (entityLabel && actionLabel) {
    return `${entityLabel} - ${actionLabel}`;
  }
  return entityLabel || actionLabel || "—";
}

export function formatAuditNotes(changes?: IAuditChange[]): string {
  const summary = summarizeChanges(changes);
  return summary || "—";
}

export function buildFirmModificationLogColumnsDef(): IHeader[] {
  return [
    {
      id: "informationUpdated",
      header: "Information Updated",
      width: 220,
    },
    { id: "source", header: "Source", width: 140 },
    { id: "userName", header: "Updated By", width: 140 },
    { id: "timestamp", header: "Date", width: 120 },
    {
      id: "notes",
      header: "Notes",
      width: 420,
      enableSorting: false,
      cell: ({ value }) => {
        const text = String(value ?? "").trim();
        if (!text || text === "—") {
          return "—";
        }
        return <span className="break-words whitespace-pre-wrap">{text}</span>;
      },
    },
  ];
}

export function buildAuditLogColumnsDef(): IHeader[] {
  return [
    {
      id: "timestamp",
      header: "Date",
      width: 170,
    },
    {
      id: "role",
      header: "Role",
      enableSorting: false,
    },
    { id: "action", header: "Action", width: 100 },
    { id: "entity", header: "Entity", width: 120 },
    { id: "userName", header: "By", width: 120 },
    { id: "origin", header: "Origin", width: 120 },
    {
      id: "changes",
      header: "Changes",
      width: 420,
      enableSorting: false,
      cell: ({ value }) => {
        const changes = (value as IAuditChange[] | undefined) ?? [];
        if (changes.length === 0) {
          return "—";
        }
        return (
          <ul className="flex flex-col gap-1">
            {changes.map((change, index) => (
              <li key={`${change.field}-${index}`} className="break-words">
                <span className="font-semibold">{change.field}</span>:{" "}
                {stringifyAuditValue(change.oldValue)} →{" "}
                {stringifyAuditValue(change.newValue)}
              </li>
            ))}
          </ul>
        );
      },
    },
  ];
}

export function formatAuditLogRows(rows: any[]): any[] {
  return rows.map((row) => ({
    ...row,
    timestamp: formatAuditTimestamp(row.timestamp),
    role: formatAuditRoles(row.role),
    action: AuditActionType[row.action as AuditActionTypeEnum] ?? row.action,
    entity: AuditEntityType[row.entity as AuditEntityTypeEnum] ?? row.entity,
  }));
}

export function formatFirmModificationLogRows(
  rows: any[],
  firmName?: string,
): any[] {
  return rows.map((row) => {
    const action =
      AuditActionType[row.action as AuditActionTypeEnum] ?? row.action;
    const entity =
      AuditEntityType[row.entity as AuditEntityTypeEnum] ?? row.entity;
    return {
      ...row,
      informationUpdated: formatInformationUpdated(entity, action),
      source: formatAuditSource(row.origin),
      userName: row.userName || "—",
      timestamp: formatAuditDateOnly(row.timestamp),
      notes: formatFirmAuditLogNote({
        entity: row.entity,
        action: row.action,
        entityId: row.entityId,
        changes: row.changes,
        firmName,
      }),
    };
  });
}

export function formatFirmModificationLogExcelRows(
  rows: any[],
  firmName?: string,
): any[] {
  return formatFirmModificationLogRows(rows, firmName);
}

export function formatAuditLogExcelRows(rows: any[]): any[] {
  return rows.map((row) => ({
    ...row,
    changes: summarizeChanges(row.changes),
  }));
}
